import { formatLongToYmd } from "./date";
import Highcharts from "highcharts";
import { formatYAxisLabel, getSidoGeoOffset, roundToTwoDecimal } from "./util";
import { DashType } from "./configs";
import { v4 as uuidv4 } from "uuid";
import {
  normalStops as designNormalStops,
  invertedStops as designInvertedStops,
  normalStopsWithoutWhite as designNormalStopsWithoutWhite,
  invertedStopsWithoutWhite as designInvertedStopsWithoutWhite,
  chart as chartDefaults,
  sizing,
} from "./design";

export const APT_NEWWINDOW_SIZE = sizing.aptNewWindowSize;
export const DefaultChartSize = sizing.defaultChartHeight;
export const ROW_SIZES = [10, 25, 50];
export const DefaultEmpty = [];
export const DefaultSidos = ["1100000000"];
export const DefaultGungus = [];
export const DefaultSido = "1100000000";
export const SidoGroupMap = new Map([
  // 1. 서울시, 경기도, 인천시
  ["1100000000", ["1100000000", "4100000000", "2800000000"]],
  ["4100000000", ["1100000000", "4100000000", "2800000000"]],
  ["2800000000", ["1100000000", "4100000000", "2800000000"]],

  // 2. 강원도
  ["5100000000", ["5100000000"]],

  // 3. 세종시, 대전시
  ["3600000000", ["3600000000", "3000000000"]],
  ["3000000000", ["3600000000", "3000000000"]],

  // 4. 충청북도, 충청남도, 경상북도
  ["4300000000", ["4300000000", "4400000000", "4700000000"]],
  ["4400000000", ["4300000000", "4400000000", "4700000000"]],
  ["4700000000", ["4300000000", "4400000000", "4700000000"]],

  // 5. 대구시
  ["2700000000", ["2700000000"]],

  // 6. 전남광주 개편 전후 비교 그룹. 전라북도는 신 지역과 구 전남 양쪽 비교군에 포함된다.
  ["1200000000", ["1200000000", "5200000000"]],
  ["5200000000", ["1200000000", "5200000000", "4600000000"]],
  ["4600000000", ["5200000000", "4600000000"]],
  ["2900000000", ["2900000000"]],

  // 8. 경상남도, 울산시, 부산시, 제주도
  ["4800000000", ["4800000000", "3100000000", "2600000000", "5000000000"]],
  ["3100000000", ["4800000000", "3100000000", "2600000000", "5000000000"]],
  ["2600000000", ["4800000000", "3100000000", "2600000000", "5000000000"]],
  ["5000000000", ["4800000000", "3100000000", "2600000000", "5000000000"]],
]);

/**
 * 지역그룹 매핑이 없는 시도도 자기 자신만 포함한 그룹으로 처리한다.
 * SidoGroupMap에 누락된 시도 ID가 들어와도 지역분석 차트 생성이 깨지지 않게 한다.
 */
export const getSidoGroupIds = (sidoId) => {
  if (!sidoId) return [];
  return SidoGroupMap.get(sidoId) ?? [sidoId];
};

export const normalStops = designNormalStops;
export const normalStopsWithoutWhite = designNormalStopsWithoutWhite;
export const invertedStopsWithoutWhite = designInvertedStopsWithoutWhite;
export const invertedStops = designInvertedStops;

const chartTitleStyle = {
  fontSize: chartDefaults.titleFontSize,
};

// PropLake logo SVG paths for Highcharts
const logoSvgPaths = {
  roof: "M68 120 L128 70 L188 120 M78 113 V138 M178 113 V138",
  mainWave: "M58 149 C82 130 105 130 128 149 C151 168 174 168 198 149",
  subWave: "M72 184 C91 171 109 171 128 184 C147 197 165 197 184 184",
};

const chartLogoColors = {
  background: "#1769E0",
  water: "#72E1E8",
};

const renderChartLogo = (chart) => {
  if (chart.proplakeLogo) {
    const { group, width, height } = chart.proplakeLogo;
    group.attr({
      translateX: chart.chartWidth - width - 5,
      translateY: chart.chartHeight - height - 5,
    });
    return;
  }
  const logoScale = 22 / 256;
  const logoWidth = 256 * logoScale;
  const logoHeight = 256 * logoScale;
  const wordmarkGap = 4;
  const padding = 5;

  const logoGroup = chart.renderer
    .g("logo")
    .attr({
      zIndex: 10,
    })
    .add();

  const wordmark = chart.renderer
    .text("Proplake", 0, 0)
    .css({
      color: chartLogoColors.background,
      fontSize: "11px",
      fontWeight: "600",
    })
    .add(logoGroup);

  const wordmarkWidth = wordmark.getBBox().width;
  const x = 0;
  const y = 0;
  const width = logoWidth + wordmarkGap + wordmarkWidth;
  chart.proplakeLogo = { group: logoGroup, width, height: logoHeight };
  logoGroup.attr({
    translateX: chart.chartWidth - width - padding,
    translateY: chart.chartHeight - logoHeight - padding,
  });

  wordmark.attr({
    x: x + logoWidth + wordmarkGap,
    y: y + logoHeight / 2 + 4,
  });

  chart.renderer
    .rect(x + 24 * logoScale, y + 24 * logoScale, 208 * logoScale, 208 * logoScale, 54 * logoScale)
    .attr({
      fill: chartLogoColors.background,
      opacity: 0.9,
    })
    .add(logoGroup);

  // 헤더 로고와 동일한 지붕과 호수 물결을 차트 우하단에 렌더링한다.
  chart.renderer
    .path()
    .attr({
      d: logoSvgPaths.roof,
      fill: "none",
      stroke: "#FFFFFF",
      "stroke-width": 18,
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      opacity: 0.9,
      transform: `translate(${x}, ${y}) scale(${logoScale})`,
    })
    .add(logoGroup);

  chart.renderer
    .path()
    .attr({
      d: logoSvgPaths.mainWave,
      fill: "none",
      stroke: "#FFFFFF",
      "stroke-width": 18,
      "stroke-linecap": "round",
      opacity: 0.9,
      transform: `translate(${x}, ${y}) scale(${logoScale})`,
    })
    .add(logoGroup);

  chart.renderer
    .path()
    .attr({
      d: logoSvgPaths.subWave,
      fill: "none",
      stroke: chartLogoColors.water,
      "stroke-width": 14,
      "stroke-linecap": "round",
      opacity: 0.9,
      transform: `translate(${x}, ${y}) scale(${logoScale})`,
    })
    .add(logoGroup);
};

/**
 * Tooltip이 잡은 현재 x축 시점의 실제 point 위치에 작은 hover marker를 표시한다.
 */
const renderChartHoverPointMarkers = (chart, points = []) => {
  if (!chart?.renderer) return;

  hideChartHoverPointMarkers(chart);

  const markerGroup = chart.renderer
    .g("hover-point-markers")
    .attr({ zIndex: 9 })
    .add();

  for (const point of points) {
    const series = point?.series;
    if (!series?.visible || point?.plotX === undefined || point?.plotY === undefined) {
      continue;
    }

    const type = series.type;
    if (!["line", "spline", "area", "areaspline", "column"].includes(type)) {
      continue;
    }

    // plotX/plotY는 plot area 기준 좌표이므로 chart offset을 더해 SVG 좌표로 변환한다.
    chart.renderer
      .circle(chart.plotLeft + point.plotX, chart.plotTop + point.plotY, 3)
      .attr({
        fill: "#ffffff",
        stroke: point.color || series.color || chartDefaults.dataLabelColor,
        "stroke-width": 2,
      })
      .add(markerGroup);
  }

  chart.customHoverPointMarkers = markerGroup;
};

/**
 * 차트 hover marker SVG group을 제거한다.
 */
const hideChartHoverPointMarkers = (chart) => {
  if (chart?.customHoverPointMarkers) {
    chart.customHoverPointMarkers.destroy();
    chart.customHoverPointMarkers = null;
  }
};

/**
 * Highcharts point hover 이벤트에서 shared tooltip이 선택한 현재 x축 points를 표시한다.
 */
const renderSharedHoverPointMarkers = function () {
  const chart = this.series?.chart;
  if (!chart) return;

  // shared tooltip에서는 같은 x축에 걸린 points가 chart.hoverPoints에 모인다.
  const points = chart.hoverPoints?.length ? chart.hoverPoints : [this];
  renderChartHoverPointMarkers(chart, points);
};

/**
 * point mouseOut 직후 다른 point로 이동 중이면 새 mouseOver가 다시 그리므로 지우기만 한다.
 */
const hideSharedHoverPointMarkers = function () {
  hideChartHoverPointMarkers(this.series?.chart);
};

export const getPlotLine = (
  value,
  { color = "black", width = 1, text = "기준선" } = {},
) => {
  return {
    value,
    color,
    width,
    dashStyle: DashType.DASH,
    id: uuidv4(), // ✅ uuid
    label: {
      text,
      color,
    },
    zIndex: 5,
  };
};

export const getTreeViewParamsFromFilter = (
  filter,
  isSingleSelection = false,
) => {
  const params = {
    selectedSidos: filter.config.selectedSidos,
    selectedGungus:
      filter.config.selectedGungus === undefined
        ? []
        : filter.config.selectedGungus,
    openedNodes: filter.config.openedNodes,
    updateProps: (props) => filter.updateConfig(props),
    updateOpens: (openedNodes) => filter.updateConfig({ openedNodes }),
    isSingleSelection,
  };
  return params;
};

export const getBarOption = (option) => {
  const options = getChartLineOption({ ...option, legend: false });
  options.chart.type = "bar";
  return options;
};

export const getColumnOption = (option) => {
  const options = getChartLineOption({ ...option, legend: false });
  options.chart.type = "column";
  return options;
};

export const getGeoMapOption = (option) => {
  const {
    map,
    gungu = false,
    min = -0.3,
    max = 0.3,
    inverted = false,
  } = option;
  const options = {
    chart: {
      map,
      height: 500,
    },
    title: {
      text: "",
      style: chartTitleStyle,
    },

    accessibility: {
      typeDescription: "Map of Germany.",
    },

    mapNavigation: {
      enabled: false,
      // buttonOptions: {
      //   verticalAlign: "bottom",
      // },
    },

    colorAxis: {
      stops: inverted ? invertedStops : normalStops,
      min,
      max,
      startOnTick: false,
      endOnTick: false,
      labels: {
        format: "{value}",
      },
    },
    tooltip: {
      formatter: function () {
        var s =
          "<br/><span style='color:" +
          this.point.color +
          "'>\u25CF</span> " +
          this.point.properties.korName +
          " : " +
          roundToTwoDecimal(this.point.value);
        return s;
      },
    },

    series: [
      {
        data: [],
        keys: ["korName", "value"],
        joinBy: "korName",
        name: "Random data",
        dataLabels: {
          enabled: !gungu,
          allowOverlap: true,
          formatter: function () {
            // 각 데이터 레이블의 위치를 조정하기 위해 offset 변수 사용
            if (this.point.properties) {
              const name = this.point.properties.korName;
              const offset = getSidoGeoOffset(name);
              // 데이터 레이블의 속성 직접 조정
              if (offset) {
                this.point.plotX = this.point.plotX + offset.x;
                this.point.plotY = this.point.plotY + offset.y;
              }
              // this.point.y = this.point.plotY;
              return name; // 레이블 포맷 변경
            }
          },
        },
      },
    ],
  };

  return options;
};

export const getChartLineOption = (option) => {
  const {
    legend = true,
    columnGroup = true,
    stacking = null,
    dateType = "W",
    handleXPoint = null,
    height = undefined,
    title = "",
    marker = false,
    markerSize = 2,
    annotations = [],
    onClick = null,
    onMouseDown = null,
    onMouseMove = null,
    onMouseUp = null,
    onResize = null,
    onLoad = null,
    onRedraw = null,
    logo = true,
  } = option;
  const options = {
    chart: {
      zoomType: null,
      // reflow: true, // 창 크기 조절 시 자동으로 크기 조정
      // height: height !== undefined ? height : 500,
      height,
      animation: false,
      events: {
        render: function () {
          if (logo) renderChartLogo(this);
        },
        resize: function () {
          const chart = this;
          // resizeAnnotations(chart);
          onResize?.(chart);
        },
        click: function (e) {
          // 차트에서의 클릭 위치를 xAxis 값으로 변환
          var x = this.xAxis[0].toValue(e.chartX);
          handleXPoint?.(x);

          onClick?.(this.chart, e);
        },
        redraw: function () {
          const chart = this;
          hideChartHoverPointMarkers(chart);
          onRedraw?.(chart);
          // 필요시 여기서 추가 로직 구현
        },
        load: function () {
          const chart = this;
          onLoad?.(chart);
          // 차트 컨테이너에 DOM 이벤트 추가
          const container = chart.container;
          container.addEventListener("mousedown", function (e) {
            onMouseDown?.(chart, e);
          });

          container.addEventListener("mousemove", function (e) {
            onMouseMove?.(chart, e);
          });

          container.addEventListener("mouseup", function (e) {
            onMouseUp?.(chart, e);
          });

          container.addEventListener("mouseleave", function () {
            hideChartHoverPointMarkers(chart);
          });
        },
      },
    },
    title: {
      text: title,
      align: "left",
      style: chartTitleStyle,
    },
    subtitle: {
      text: "",
      align: "left",
    },
    xAxis: [],
    yAxis: [],
    tooltip: {
      shared: true,
      /**
       * Forward Fill Tooltip: 마우스 오버 시점(this.x)에 데이터가 없는 series는
       * 해당 시점 이전의 가장 가까운 값을 찾아 표시한다.
       * 데이터 자체는 변경하지 않으므로 column/line 차트 모양에 영향 없음.
       */
      formatter: function () {
        // scatter용 tooltip (단일 point)
        if (!this.points && this.point) {
          const point = this.point;
          let tooltipHtml = formatLongToYmd(this.x, dateType);
          tooltipHtml +=
            "<br/><span style='color:" +
            point.color +
            "'>\u25CF</span> " +
            point.series.name +
            " : " +
            formatYAxisLabel(roundToTwoDecimal(point.y));
          return tooltipHtml;
        }
        let tooltipHtml = formatLongToYmd(this.x, dateType);
        const pointMap = new Map();
        for (var point of this.points) {
          pointMap.set(point.series.index, point);
        }

        const chart = this.points[0]?.series?.chart;
        if (chart) {
          const currentX = this.x;
          for (const series of chart.series) {
            if (!series.visible) continue;
            // 실거래 spotter처럼 점을 직접 hover할 때만 보여야 하는 계열은 제외한다.
            if (series.options.excludeFromSharedTooltip) continue;

            const point = pointMap.get(series.index);
            let value = point?.y ?? null;
            const color = point?.color || series.color;

            if (value === null) {
              // 현재 x 이하인 데이터 중 가장 가까운 값을 찾아 tooltip에만 표시한다.
              for (let i = series.data.length - 1; i >= 0; i--) {
                const p = series.data[i];
                if (p.x <= currentX && p.y != null) {
                  value = p.y;
                  break;
                }
              }
            }

            if (value !== null) {
              tooltipHtml +=
                "<br/><span style='color:" +
                color +
                "'>\u25CF</span> " +
                series.name +
                " : " +
                formatYAxisLabel(roundToTwoDecimal(value));
            }
          }
        }
        return tooltipHtml;
      },
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      // x: 130,
      // y: -17,
      // floating: true,
      backgroundColor:
        Highcharts.defaultOptions.legend.backgroundColor || // theme
        chartDefaults.legendBackground,
      navigation: {
        activeColor: chartDefaults.legendActiveColor,
        animation: true,
        arrowSize: 12,
        inactiveColor: chartDefaults.legendInactiveColor,
        style: {
          fontWeight: "bold",
          color: chartDefaults.legendFontColor,
          fontSize: chartDefaults.legendFontSize,
        },
      },
    },
    plotOptions: {
      line: {
        stacking,
        point: {
          events: {
            mouseOver: renderSharedHoverPointMarkers,
            mouseOut: hideSharedHoverPointMarkers,
          },
        },
      },
      area: {
        stacking,
        point: {
          events: {
            mouseOver: renderSharedHoverPointMarkers,
            mouseOut: hideSharedHoverPointMarkers,
          },
        },
      },
      column: {
        grouping: columnGroup, // 그룹화 비활성화
        stacking,
        point: {
          events: {
            mouseOver: renderSharedHoverPointMarkers,
            mouseOut: hideSharedHoverPointMarkers,
          },
        },
      },
      series: {
        animation: false,
        point: {
          events: {
            mouseOver: renderSharedHoverPointMarkers,
            mouseOut: hideSharedHoverPointMarkers,
          },
        },
        dataLabels: {
          enabled: true,
          allowOverlap: false,
        },
        showInLegend: legend,
        marker: {
          enabled: marker, // 마커 활성화
          radius: markerSize, // 마커 크기 조절
          states: {
            hover: {
              enabled: true,
            },
          },
        },
        states: {
          hover: {
            enabled: true, // hover 상태에서의 시리즈 강조 효과 비활성화
          },
          inactive: {
            enabled: false, // <<=== 이 설정이 핵심!
          },
        },
      },
    },
    series: null,
    annotations,
    credits: {
      enabled: !logo, // 로고 사용 시 기본 크레딧 비활성화
    },
  };
  return options;
};

export const getHeatmapOption = () => {
  const option = {
    chart: {
      type: "heatmap",
      // height: "700",
      // width: "2000",
      // marginTop: 40,
      // marginBottom: 80,
      plotBorderWidth: 0,
      animation: false,
      // 차트 렌더링 이벤트
      events: {
        render: function () {
          // 로딩 UI를 표시
          console.log("loading...");
        },
        load: function () {
          // 로딩 UI를 숨기거나 제거
          console.log("loaded");
        },
      },

      // 로딩 상태 설정
      lang: {
        loading: "로딩 중...", // 로딩 중일 때 표시될 메시지
      },
      loading: {
        showDuration: 1000, // 로딩 중 메시지를 표시할 최소 시간 (밀리초)
      },
    },

    boost: {
      useGPUTranslations: true,
      // Chart-level boost when there are more than 5 series in the chart
      seriesThreshold: 5,
    },

    title: {
      text: null,
      style: chartTitleStyle,
    },

    xAxis: {
      categories: [],
    },

    yAxis: {
      categories: [],
      reversed: true,
      title: "시도",
    },

    plotOptions: {
      heatmap: {
        borderWidth: 0, // 테두리 너비를 0으로 설정하여 비활성화합니다.
        borderColor: "transparent",
      },
    },

    // accessibility: {
    //   point: {
    //     descriptionFormat:
    //       "{(add index 1)}. " +
    //       "{series.xAxis.categories.(x)} sales " +
    //       "{series.yAxis.categories.(y)}, {value}.",
    //   },
    // },

    colorAxis: {
      stops: designNormalStops,
      min: -0.3,
      max: 0.3,
      // startOnTick: false,
      // endOnTick: false,
      labels: {
        format: "{value}",
      },
    },

    legend: {
      symbolWidth: 300,
      y: 20,
    },

    tooltip: {
      format:
        "<b>{series.xAxis.categories.(point.x)}</b> sold<br>" +
        "<b>{point.value}</b> items on <br>" +
        "<b>{series.yAxis.categories.(point.y)}</b>",
    },

    series: [
      {
        name: "Sales per employee",
        // borderWidth: 1,
        data: [],
        dataLabels: {
          enabled: false,
          color: chartDefaults.dataLabelColor,
        },
      },
    ],

    // responsive: {
    //   rules: [
    //     {
    //       condition: {
    //         maxWidth: 500,
    //       },
    //       chartOptions: {
    //         yAxis: {
    //           labels: {
    //             format: "{substr value 0 1}",
    //           },
    //         },
    //       },
    //     },
    //   ],
    // },
  };
  return option;
};

export const getTilemapOption = () => {
  const option = {
    chart: {
      type: "tilemap",
      inverted: true,
      height: "1000",
      // width: "1000",
      // width: "100%",
    },

    title: {
      text: null,
      style: chartTitleStyle,
    },

    // subtitle: {
    //   text: "Test",
    // },

    xAxis: {
      visible: false,
    },

    yAxis: {
      visible: false,
    },

    // colorAxis: {
    //   dataClasses: getTilemapColors(),
    //   visible: false,
    // },

    colorAxis: {
      stops: designNormalStops,
      min: -0.3,
      max: 0.3,
      startOnTick: false,
      endOnTick: false,
      labels: {
        format: "{value}",
      },
    },

    legend: {
      symbolWidth: 300,
      y: 20,
    },

    tooltip: {
      headerFormat: "",
      pointFormat:
        "The population of <b> {point.name}</b> is <b>{point.value}</b>",
    },

    plotOptions: {
      series: {
        dataLabels: {
          align: "center",
          enabled: true,
          format: "{point.name}",
          color: chartDefaults.dataLabelColor,
          style: {
            textOutline: false,
            whiteSpace: "normal",
            textOverflow: "none",
          },
        },
      },
    },

    series: [],
  };
  return option;
};

export const getTilemapColors = () => {
  // Define colors
  const darkBlue = [0, 0, 139]; // Dark blue color
  const darkRed = [139, 0, 0]; // Dark red color

  const white = [255, 255, 255]; // White color

  // Create gradient from -3.0 to 0 (dark blue to white)
  const negativeGradient = gradientSteps(-0.3, 0, 30, darkBlue, white);

  // Create gradient from 0 to 3.0 (white to dark blue)
  const positiveGradient = gradientSteps(0, 0.3, 30, white, darkRed);

  // edge 값 보정
  negativeGradient[0].from = -1000;
  positiveGradient[positiveGradient.length - 1].to = 1000;

  // Combine both gradients
  const fullGradient = negativeGradient.concat(positiveGradient);

  return fullGradient;
};

const interpolateColor = (color1, color2, factor = 0.5) => {
  var result = color1.slice();
  for (var i = 0; i < 3; i++) {
    result[i] = Math.round(result[i] + factor * (color2[i] - color1[i]));
  }
  return result;
};

const rgbToHex = (r, g, b) => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const gradientSteps = (start, end, steps, colorStart, colorEnd) => {
  var gradientArray = [];
  for (var i = 0; i <= steps; i++) {
    var factor = i / steps;
    var colorInterpolated = interpolateColor(colorStart, colorEnd, factor);
    var fromValue = (start + (end - start) * (i / steps)).toFixed(2);
    var toValue = (start + (end - start) * ((i + 1) / steps)).toFixed(2);

    gradientArray.push({
      from: parseFloat(fromValue),
      to: parseFloat(toValue),
      color: rgbToHex(
        colorInterpolated[0],
        colorInterpolated[1],
        colorInterpolated[2],
      ),
      name: `${fromValue} - ${toValue}`,
    });
  }
  return gradientArray;
};

export const adminFuncs = [
  {
    label: "DeleteHistory",
    value: "deletehistory",
  },
  {
    label: "ClearCache",
    value: "clearcache",
  },
  {
    label: "ReloadCache",
    value: "reloadcache",
  },
];

export const adminActions = [
  {
    label: "All(3day)",
    value: "All",
  },
  {
    label: "Naver",
    value: "Naver",
  },
  {
    label: "SaleCount",
    value: "SaleCount",
  },
  {
    label: "Public",
    value: "Public",
  },
  {
    label: "Price",
    value: "Price",
  },
  {
    label: "RepresentPyeong",
    value: "RepresentPyeong",
  },

  {
    label: "Neighbors",
    value: "Neighbors",
  },
  {
    label: "AllRealPrice",
    value: "AllRealPrice",
  },
  {
    label: "YearRealPrice",
    value: "YearRealPrice",
  },
  {
    label: "RealPrice",
    value: "RealPrice",
  },
  {
    label: "PriceChart",
    value: "PriceChart",
  },
  {
    label: "AllPriceChart",
    value: "AllPriceChart",
  },
  {
    label: "PriceTable",
    value: "PriceTable",
  },
  {
    label: "AllPriceTable",
    value: "AllPriceTable",
  },
  {
    label: "AllSubRate",
    value: "AllSubRate",
  },
  {
    label: "AllDBSaleCount",
    value: "AllDBSaleCount",
  },
  {
    label: "DBSaleCount",
    value: "DBSaleCount",
  },
  {
    label: "AsilSaleCount",
    value: "AsilSaleCount",
  },
  {
    label: "SpwSaleCount",
    value: "SpwSaleCount",
  },
];

export const getBorderOption = (
  item,
  thickColor = "black",
  thinColor = "black",
  borderWidth = 3,
  thinBorderWidth = 1,
) => {
  return {
    borderLeft:
      item.border && item.border.left
        ? borderWidth
        : item.thinBorder && item.thinBorder.left
          ? thinBorderWidth
          : undefined,
    borderLeftColor: item.border && item.border.left ? thickColor : thinColor,
    borderTop:
      item.border && item.border.top
        ? borderWidth
        : item.thinBorder && item.thinBorder.top
          ? thinBorderWidth
          : undefined,
    borderTopColor: item.border && item.border.top ? thickColor : thinColor,
    borderRight:
      item.border && item.border.right
        ? borderWidth
        : item.thinBorder && item.thinBorder.right
          ? thinBorderWidth
          : undefined,
    borderRightColor: item.border && item.border.right ? thickColor : thinColor,
    borderBottom:
      item.border && item.border.bottom
        ? borderWidth
        : item.thinBorder && item.thinBorder.bottom
          ? thinBorderWidth
          : undefined,
    borderBottomColor:
      item.border && item.border.bottom ? thickColor : thinColor,
  };
};
